import {
  EmployeePatchRequest,
  EmployeePostRequest,
} from '~/common/entities/Employees/EmployeeRequest';
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import {
  EmployeeRoles,
  Role,
  RoleCode,
} from '~/common/entities/Roles/RoleTypes';
import { EmployeeResponse } from '~/common/entities/Employees/EmployeeResponse';
import { EmployeeName } from '~/common/entities/Employees/EmployeeTypes';
import { Employee } from '~/common/entities/Department/DepartmentTypes';
import { IdProvider, unwrap } from '../common/apiCommons';
import axios from 'axios';

interface CurrentEmployeeStatus {
  employee: number;
  active: boolean;
  is_in_pause: boolean;
  current_job: number | null | undefined;
  current_job_type: 'RJ' | 'RR' | 'RS' | 'NJ';
}

export enum EmployeeKeys {
  EMPLOYEES = 'EMPLOYEES',
  EMPLOYEES_STATUS = 'EMPLOYEES_STATUS',
}

const EmployeeQueries = {
  async getEmployeeName(id: string) {
    const res = await axios.get<EmployeeName>(`/employee/${id}/name/`);
    return unwrap(res);
  },
  async getEmployees() {
    const res = await axios.get<Employee[]>('/company/users/');
    return unwrap(res);
  },
  async getCurrentEmployeesStatus() {
    const res = await axios.get<CurrentEmployeeStatus[]>('/employees/');
    return unwrap(res);
  },
  async getRoles() {
    const res = await axios.get<Role[]>('/roles/');
    return unwrap(res);
  },
  async getRolesOfEmployee(employee: IdProvider) {
    if (!employee?.id) {
      return null;
    }
    const res = await axios.get<EmployeeRoles[]>(`/roles/${employee.id}/`);
    return unwrap(res);
  },
};

const EmployeeMutations = {
  async addEmployee(request: EmployeePostRequest) {
    const res = await axios.post<Employee>('/auth/register/', request);
    return unwrap(res);
  },
  async deleteEmployee(employee: IdProvider) {
    if (!employee?.id) {
      return;
    }
    const res = await axios.delete<void>(`/employee/${employee.id}/`);
    return unwrap(res);
  },
  async addRoleToEmployee(params: { employee: Employee; roleCode: RoleCode }) {
    const res = await axios.post<EmployeeRoles[]>(
      `/roles/${params.employee.id}/${params.roleCode}/`,
    );
    return unwrap(res);
  },
  editEmployee({
    employee,
    request,
  }: {
    employee: Employee;
    request: EmployeePatchRequest;
  }) {
    return axios.patch<EmployeeResponse>(`/employee/${employee.id}/`, request);
  },
};

const EmployeeHooks = {
  useEmployeeName(id: string, options?: UseQueryOptions<EmployeeName>) {
    return useQuery({
      queryKey: ['employeeName', id],
      queryFn: () => EmployeeQueries.getEmployeeName(id),
      ...(options ?? {}),
    });
  },
  useEmployeeQuery(options?: UseQueryOptions<Employee[]>) {
    const queryClient = useQueryClient();
    const result = useQuery({
      queryKey: [EmployeeKeys.EMPLOYEES],
      queryFn: EmployeeQueries.getEmployees,
      ...(options ?? {}),
    });
    const QueryResultAndInvalidate: [typeof result, () => Promise<void>] = [
      result,
      () => queryClient.invalidateQueries([EmployeeKeys.EMPLOYEES]),
    ];
    return QueryResultAndInvalidate;
  },
  useCurrentEmployeeStatus(options?: UseQueryOptions<CurrentEmployeeStatus[]>) {
    const queryClient = useQueryClient();
    const result = useQuery({
      queryKey: [EmployeeKeys.EMPLOYEES_STATUS],
      queryFn: EmployeeQueries.getCurrentEmployeesStatus,
      ...(options ?? {}),
    });
    const QueryResultAndInvalidate: [typeof result, () => Promise<void>] = [
      result,
      () => queryClient.invalidateQueries([EmployeeKeys.EMPLOYEES_STATUS]),
    ];
    return QueryResultAndInvalidate;
  },
  useRoles(options?: UseQueryOptions<Role[]>) {
    return useQuery({
      queryKey: ['roles'],
      queryFn: () => EmployeeQueries.getRoles(),
      ...(options ?? {}),
    });
  },
  useRolesOfEmployee(
    employee: IdProvider,
    options?: UseQueryOptions<EmployeeRoles[] | null>,
  ) {
    return useQuery({
      queryKey: ['roles', employee?.id],
      queryFn: () => EmployeeQueries.getRolesOfEmployee(employee),
      ...(options ?? {}),
    });
  },
  useAddRole() {
    const queryClient = useQueryClient();
    return useMutation(EmployeeMutations.addRoleToEmployee, {
      async onSuccess(_, params) {
        await queryClient.invalidateQueries(['roles', params.employee.id]);
      },
    });
  },
  useAddEmployee() {
    const queryClient = useQueryClient();
    return useMutation(EmployeeMutations.addEmployee, {
      async onSuccess() {
        await queryClient.invalidateQueries([EmployeeKeys.EMPLOYEES]);
        await queryClient.invalidateQueries([EmployeeKeys.EMPLOYEES_STATUS]);
      },
    });
  },
  useDeleteEmployee() {
    const queryClient = useQueryClient();
    return useMutation(EmployeeMutations.deleteEmployee, {
      async onSuccess() {
        await queryClient.invalidateQueries([EmployeeKeys.EMPLOYEES]);
        await queryClient.invalidateQueries([EmployeeKeys.EMPLOYEES_STATUS]);
      },
    });
  },
  useEditEmployee() {
    const queryClient = useQueryClient();
    return useMutation(EmployeeMutations.editEmployee, {
      async onSuccess() {
        await queryClient.invalidateQueries([EmployeeKeys.EMPLOYEES]);
        await queryClient.invalidateQueries([EmployeeKeys.EMPLOYEES_STATUS]);
      },
    });
  },
};

const EmployeeApi = {
  queries: EmployeeQueries,
  mutations: EmployeeMutations,
  hooks: EmployeeHooks,
};

export default EmployeeApi;
